import React from "react";
import { DotLegalAlert, DotLegalFullScreenSpinner, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import TemplateStepFields from "../../entityTemplates/templateStepFields/TemplateStepFields";
import EntityTemplateOption from "../../entityTemplates/EntityTemplateOption";
import { useAddProcessPane } from "./AddProcessPane.hooks";
import ProcessTemplateField from "./ProcessTemplateField";
import { AddProcessPaneProps } from "./AddProcessPane.types";
import TrialUsageWarning from "@common/components/TrialUsageWarning";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";

function AddProcessPane(props: AddProcessPaneProps) {
    const { translateString } = useTranslation();
    const { platformFeatures } = usePlanContext();
    const {
        saveModel,
        modelUpdater,
        onSaveClick,
        templates,
        templatesIsLoading,
        lockedTemplateTexts,
        isLoadingTemplate,
        selectedTemplateMissingAccess,
        fieldGroups,
        editedProcess,
        selectedTagIds,
        requiredFieldValidationErrors,
        validation,
        isSaving,
    } = useAddProcessPane();

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={translateString("createProcess")}
            okButtonText={translateString("create")}
            onOkClick={onSaveClick}
            inProgress={isSaving}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <DotLegalSelect
                options={templates}
                onChange={modelUpdater.onTemplateChange}
                label={translateString("processTemplate")}
                noOptionsLabel={translateString("noOptions")}
                isLoading={templatesIsLoading}
                toolTipText={translateString("addProcessTemplateTooltip")}
                disableClearable
                selectedItem={saveModel.entityTemplateId === "" ? null : saveModel.entityTemplateId}
                errorText={validation.getErrorsForDisplay("entityTemplateId")}
                renderOptions={(option) => <EntityTemplateOption name={option.name} lockedText={lockedTemplateTexts.get(option.id)} />}
            />

            {selectedTemplateMissingAccess && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                </Box>
            )}

            {isLoadingTemplate && <DotLegalFullScreenSpinner />}

            {!selectedTemplateMissingAccess && (
                <TemplateStepFields
                    groups={fieldGroups}
                    renderField={(field) => (
                        <ProcessTemplateField
                            field={field}
                            name={saveModel.name}
                            onNameChange={modelUpdater.onNameChange}
                            nameErrorText={validation.getErrorsForDisplay("name")}
                            process={editedProcess}
                            onChange={modelUpdater.onTemplateFieldChange}
                            validationErrors={requiredFieldValidationErrors}
                            selectedTagIds={selectedTagIds}
                            onTagsChange={modelUpdater.onTagsChange}
                        />
                    )}
                />
            )}
            {props.shouldDisplayCreateProcessWarning && (
                <TrialUsageWarning
                    textKey={"createExceedingNumberOfProcessesWarning"}
                    values={{ count: platformFeatures.maxNumberOfProcesses }}
                    marginTop={2}
                />
            )}
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddProcessPane });
